/**
 * Utility functions for GraphQL schema discovery and query building
 */

export interface GraphQLField {
  name: string;
  type: {
    name?: string;
    kind: string;
  };
}

export interface GraphQLType {
  name: string;
  fields: GraphQLField[];
}

/**
 * Build a dynamic GraphQL query based on available fields
 * @param organizationIdParam - Whether to include organizationId parameter
 * @param availableFields - Array of field names that are available on the type
 * @returns GraphQL query string
 */
export const buildMediaQuery = (
  organizationIdParam = true,
  availableFields: string[] = ['id', 'updatedAt', 'locationId', 'objectType']
): string => {
  const params = organizationIdParam ? '($organizationId: String!)' : '';
  const args = organizationIdParam ? '(organizationId: $organizationId)' : '';
  
  return `
    query GetMediaByOrganization${params} {
      media${args} {
        ${availableFields.join('\n        ')}
      }
    }
  `;
};

/**
 * Map GraphQL field types to determine which fields should be queried
 * @param fields - Array of GraphQL fields from introspection
 * @returns Array of field names that are safe to query
 */
export const getSafeFieldsToQuery = (fields: GraphQLField[]): string[] => {
  const safeFields: string[] = [];
  
  fields.forEach(field => {
    // Include scalar fields and basic types
    if (
      field.type.kind === 'SCALAR' || 
      field.type.name === 'String' ||
      field.type.name === 'ID' ||
      field.type.name === 'Int' ||
      field.type.name === 'Boolean' ||
      field.type.name === 'Float'
    ) {
      safeFields.push(field.name);
    }
  });
  
  return safeFields;
};

/**
 * Check if a specific field exists in the GraphQL type
 * @param fields - Array of GraphQL fields from introspection
 * @param fieldName - Name of the field to check
 * @returns boolean indicating if field exists
 */
export const hasField = (fields: GraphQLField[], fieldName: string): boolean => {
  return fields.some(field => field.name === fieldName);
};

/**
 * Get field mapping for transforming API response to expected interface
 * @param availableFields - Fields available from API
 * @param expectedFields - Fields expected by the interface
 * @returns Mapping object for field transformation
 */
export const getFieldMapping = (
  availableFields: string[],
  expectedFields: string[]
): Record<string, string | null> => {
  const mapping: Record<string, string | null> = {};
  
  expectedFields.forEach(expected => {
    if (availableFields.includes(expected)) {
      mapping[expected] = expected; // Direct mapping
    } else {
      mapping[expected] = null; // Field not available, will use default
    }
  });
  
  return mapping;
};

/**
 * Default values for Design interface fields when not available from API
 * These are now dynamic and context-aware rather than hardcoded
 */
export const DESIGN_FIELD_DEFAULTS = {
  id: '',
  project: 'Conference Room', // Default to Conference Room for Conference Room Designer
  locationId: '',
  objectType: 'Conference Room', // Dynamic based on tool context
  designer: '', // Will be populated from current user context
  updatedAt: new Date().toISOString(),
  revision: '1',
  thumbnailUrl: undefined
} as const;

/**
 * Get dynamic design field defaults based on context
 * @param context - Context object containing tool info, user info, etc.
 * @returns Dynamic defaults object
 */
export const getDynamicDesignDefaults = (context?: {
  toolName?: string;
  userName?: string;
  designType?: string;
}) => {
  const currentUser = context?.userName || sessionStorage.getItem('username') || '';

  return {
    id: '',
    project: context?.toolName === 'conferenceroomDesigner' ? 'Conference Room' :
             context?.toolName === 'classroomDesigner' ? 'Classroom' :
             context?.toolName === 'warehouseDesigner' ? 'Warehouse' :
             'Room Design', // Default fallback
    locationId: '',
    designType: context?.designType ||
                (context?.toolName === 'conferenceroomDesigner' ? 'Conference Room' : 'Room Design'),
    designer: currentUser,
    updatedAt: new Date().toISOString(),
    revision: '1',
    thumbnailUrl: undefined
  };
};
